import React, { useEffect, useRef, useState } from 'react';
import { TonConnectUI } from '@tonconnect/ui';
import styles from './PaymentComponent.module.css';
import { fetchData } from '../../api/api';
import { handleError } from '../../context/errors/errorUtils';
import { useAppConfig } from '../../context/AppConfigContext';
import Modal from '../Modal';
import PaymentTributeComponent from './PaymentTributeComponent';

let globalTonConnectUI = null;

const PaymentComponent = ({ tariff = 1, tariffConfig, isBrowser, onClose }) => {
  const { site, t, userInfo } = useAppConfig();
  const tonConnectRef = useRef(null);
  const [tonConnectUI, setTonConnectUI] = useState(null);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [tonRate, setTonRate] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transactionStatus, setTransactionStatus] = useState(null);
  const [modal, setModal] = useState(null);
  //const currentTariff = tariffConfig[tariff] || tariffConfig[1];

  const currentTariff = tariffConfig;
  
  // Добавляем проверку на случай, если tariffConfig не передан
  if (!currentTariff || !currentTariff.name) {
    return (
      <div className={styles.modal}>
        <div className={styles.header}>
          <h3>Error</h3>
          <button className={styles.closeButton} onClick={onClose}>&times;</button>
        </div>
        <div className={styles.container}>
          <p>Tariff configuration is missing</p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    if (globalTonConnectUI) {
      setTonConnectUI(globalTonConnectUI);
      globalTonConnectUI.onStatusChange(wallet => {
        setButtonDisabled(!wallet);
      });
      return;
    }

    try {
      const tonConnect = new TonConnectUI({
        manifestUrl: `${site}/manifest.json`,
        buttonRootId: 'ton-connect',
      });

      globalTonConnectUI = tonConnect;
      setTonConnectUI(tonConnect);
      tonConnect.onStatusChange(wallet => {
        setButtonDisabled(!wallet);
      });
    } catch (err) {
      handleError({
        code: 'ERROR_TON_CONNECT_INIT',
        error: err,
        context: {
          module: 'PaymentComponent',
          message: 'TON Connect init error',
        },
        showToUser: true,
      });
      setModal({
        title: t.payment.initErrorTitle,
        children: <p>{t.payment.initError}</p>,
        onClose: () => setModal(null),
      });
    }
  }, []);

  useEffect(() => {
    const fetchTonRate = async () => {
      const cacheKey = 'ton_rate_cache';
      const cacheTime = 5 * 60 * 1000;

      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const { rate, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < cacheTime) {
          setTonRate(rate);
          return;
        }
      }

      try {
        const response = await fetch('https://min-api.cryptocompare.com/data/price?fsym=TON&tsyms=USD');
        const data = await response.json();
        const rate = data.USD;

        setTonRate(rate);
        localStorage.setItem(cacheKey, JSON.stringify({ rate, timestamp: Date.now() }));
      } catch (err) {
        handleError({
          code: 'ERROR_FETCH_TON_RATE',
          error: err,
          context: {
            module: 'PaymentComponent',
            message: 'Fetch TON rate error',
          },
        });
      }
    };

    fetchTonRate();
  }, []);

  const handleStarsPay = async () => {
    setModal({
      title: t.payment.stars,
      children: <p>{t.payment.confirmStars(currentTariff.starsPrice, currentTariff.name)}</p>,
      onClose: () => {
        setModal(null);
        setIsProcessing(false);
      },
      onConfirm: async () => {
        setModal(null);
        await proceedWithStarsPayment();
      },
      confirmText: t.payment.confirm,
    });
  };

  const proceedWithStarsPayment = async () => {
    setIsProcessing(true);
    try {
      const response = await sendPaymentConfirmation('stars');
      if (response.success) {
        window.Telegram.WebApp.close();
      } else {
        throw new Error('Invoice creation failed');
      }
    } catch (error) {
      handleError({
        code: 'ERROR_STARS_PAYMENT',
        error,
        context: {
          module: 'PaymentComponent',
          message: 'Stars payment error',
        },
        showToUser: true,
      });
      setModal({
        title: t.payment.error,
        children: <p>{t.payment.starsError}</p>,
        onClose: () => setModal(null),
        onConfirm: () => setModal(null),
      });
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleTonPay = async () => {
    if (!tonConnectUI || tonRate === null) return;

    setIsProcessing(true);
    setTransactionStatus('pending');

    try {
      const tonAmount = currentTariff.usdPrice / tonRate;
      const nanoTonAmount = Math.floor(tonAmount * 1e9).toString();

      const tx = await tonConnectUI.sendTransaction({
        validUntil: Math.floor(Date.now() / 1000) + 60,
        messages: [
          {
            address: 'UQDVPmKWC1oWhU3Z-rMXOdTYMBEn1b7cAJuU31g6F3g25O_n',
            amount: nanoTonAmount,
          },
        ],
      });

      if (tx?.boc) {
        setTransactionStatus('sent');
		sendPaymentConfirmation('ton', null, currentTariff.usdPrice, tonAmount);
		
        setModal({
          title: t.payment.sentTitle,
          children: (
            <p>
              {t.payment.sentDesc}
              <br />
              {t.payment.waitTelegram}
            </p>
          ),
          onConfirm: onClose,
          hideCancel: true,
        });
      }
    } catch (error) {
      handleError({
        code: 'ERROR_TON_PAYMENT',
        error,
        context: {
          module: 'PaymentComponent',
          message: 'TON payment error',
        },
        showToUser: true,
      });
      setTransactionStatus('error');
      setModal({
        title: t.payment.error,
        children: <p>{t.payment.tonError}</p>,
        onConfirm: () => setModal(null),
        onClose: () => setModal(null),
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const sendPaymentConfirmation = async (method, transactionId = null, amount = null, tonAmount = null) => {
    try {
      return await fetchData('/api/payment-confirmation', 'POST', {
        userId: window.Telegram?.WebApp?.initDataUnsafe?.user?.id,
        paymentMethod: method,
        tariff,
        transactionId,
        amount: amount || (method === 'ton' ? currentTariff.usdPrice : currentTariff.starsPrice),
        tonAmount: tonAmount,
      });
    } catch (error) {
      handleError({
        code: 'ERROR_PAYMENT_CONFIRMATION',
        error,
        context: {
          module: 'PaymentComponent',
          message: 'Payment confirm error',
          method,
          transactionId,
        },
      });
      throw error;
    }
  };

  const getTransactionStatusText = () => {
    const statuses = {
      pending: t.payment.statuses.pending,
      sent: t.payment.statuses.sent,
      error: t.payment.statuses.error,
      timeout: t.payment.statuses.timeout,
    };
    return statuses[transactionStatus] || '';
  };

  return (
    <div className={styles.modal}>
      <div className={styles.header}>
        <h3 className={styles.heading}>{t.payment.heading} "{currentTariff.name}"</h3>
        <button className={styles.closeButton} onClick={onClose}>&times;</button>
      </div>
      <div className={styles.container}>
        <p className={styles.price}>{t.payment.price}: ${currentTariff.usdPrice}</p>
		<p className={styles.info}>
		  {t.payment.methodTitle}
		  <br /><br />
		  {/* Для всех показываем Tribute */}
		  <strong>1. {t.payment.tribute}:</strong> {t.payment.tributeDesc}
		  <br /><br />

		  {/* Для Telegram WebApp / Native показываем остальные */}
		  {!isBrowser && (
			<>
			  <strong>2. {t.payment.ton}:</strong> {t.payment.tonDesc}
			  <br /><br />
			  <strong>3. {t.payment.stars}:</strong> {t.payment.starsDesc}
			  <br /><br />
			</>
		  )}
		</p>

		
		<div className={styles.tabs}>
		  {/* Только для браузеров показываем Tribute */}
		  <PaymentTributeComponent tariff={tariff} onPay={onClose} />

		  {/* TON */}
		  {!isBrowser && (
			<button
			  className={`${styles.tabButton} ${paymentMethod === 'ton' ? styles.active : ''}`}
			  onClick={() => setPaymentMethod('ton')}
			>
			  {t.payment.ton}
			</button>
		  )}

		  {/* Stars */}
		  {!isBrowser && (
			<button
			  className={`${styles.tabButton} ${paymentMethod === 'stars' ? styles.active : ''}`}
			  onClick={() => setPaymentMethod('stars')}
			>
			  {t.payment.stars}
			</button>
		  )}
		</div>


        <div
          id="ton-connect"
          ref={tonConnectRef}
          className={paymentMethod === 'ton' ? styles.tonConnect : styles.hidden}
        />

        {!isBrowser && paymentMethod === 'ton' && (
          <div className={styles.buttonWrapper}>
            <button
              onClick={handleTonPay}
              disabled={buttonDisabled || tonRate === null || isProcessing}
              className={`${styles.payButton} ${styles.tonButton} ${buttonDisabled || isProcessing ? styles.disabled : ''}`}
            >
              {isProcessing
                ? t.payment.processing
                : t.payment.payTon((currentTariff.usdPrice / (tonRate || 1)).toFixed(2))}
            </button>
          </div>
        )}

        {!isBrowser && paymentMethod === 'stars' && (
          <button
            onClick={handleStarsPay}
            disabled={isProcessing}
            className={`${styles.payButton} ${styles.starButton} ${isProcessing ? styles.disabled : ''}`}
          >
            {isProcessing
              ? t.payment.processing
              : t.payment.payStars(currentTariff.starsPrice)}
          </button>
        )}

        {transactionStatus && (
          <div className={styles.transactionStatus}>
            {getTransactionStatusText()}
          </div>
        )}
      </div>

      {modal && (
        <Modal
          title={modal.title}
          onClose={modal.onClose}
          onConfirm={modal.onConfirm}
          confirmText={modal.confirmText || t.payment.ok}
          hideCancel={modal.hideCancel}
        >
          {modal.children}
        </Modal>
      )}
    </div>
  );
};

export default PaymentComponent;

/*
<em>{t.payment.tip}</em>
*/